import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import { docHint, docLink } from '../../lib/answerReviewView';
import type { BotRef, BotSystem } from '../../lib/botNav';
import { fmtTime } from '../../lib/jobView';
import { useJobStream } from '../knowledge/useJobStream';
import {
  VERDICT_LABEL,
  VERDICT_TONE,
  blockLine,
  blockVerdict,
  botTokens,
  flowV2Notice,
  fmtDuration,
  fmtUsd,
  isActiveRun,
  itemsRefreshWait,
  runProgress,
  rateLabel,
  scoreText,
  suggestOutcomeText,
  wrongBlocksFirst,
  type Run,
  type RunItem,
} from '../../lib/testerView';

const ENDED = ['succeeded', 'failed', 'cancelled'];

/**
 * One test run (spec §4.4): live progress while it runs (the hub relays llm-tester's job stream — the run continues if
 * this page closes), then the result: wrong answers first, each with the question, the answer, why it was judged wrong
 * and the documents it came from, and "Suggest fixes". Cost is internal (Claude scoring + the bot's own answer
 * tokens), never billed to the customer.
 */
export function RunView({ system, botId, jobId, onBack }: { system: BotSystem; botId: string; jobId: string; onBack: () => void }) {
  const bot = useMemo<BotRef>(() => ({ system, botId }), [system, botId]);
  const [run, setRun] = useState<Run | null>(null);
  const [items, setItems] = useState<RunItem[] | null>(null);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [flow, setFlow] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [r, it] = await Promise.all([hubClient.testerRun(bot, jobId), hubClient.testerRunItems(bot, jobId)]);
      setRun(r);
      setItems(it.items);
    } catch (e) {
      setError(errorText(e));
    }
  }, [bot, jobId]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    hubClient
      .testerEstimate(bot)
      .then((e) => setFlow(e.flowVersion))
      .catch(() => setFlow(null));
  }, [bot]);

  useEffect(() => {
    hubClient
      .testerBlockLinks(bot)
      .then((ls) => setTitles(Object.fromEntries(ls.map((l) => [l.blockRef, l.title]))))
      .catch(() => setTitles({})); // refs are then shown as they are
  }, [bot]);

  const active = isActiveRun(run?.status);
  const live = useJobStream(system, botId, active ? jobId : null);
  const ended = ENDED.includes(live.status);
  useEffect(() => {
    if (ended) void load();
  }, [ended, load]);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const cancel = () =>
    act(async () => {
      await hubClient.testerCancelRun(bot, jobId);
      await load();
    });
  const suggest = () =>
    act(async () => {
      setOutcome(null);
      const o = await hubClient.testerSuggest(bot, jobId);
      // Still working upstream: keep the button off for this run — a second click would only get a 409.
      if (o.state === 'working') setSuggesting(true);
      setOutcome(suggestOutcomeText(o));
    });

  const s = run?.summary ?? null;
  const p = runProgress(live.events, s?.total ?? 0);
  // While the run is active the answer list follows the progress (it used to load only on open and at the end).
  const lastRefresh = useRef({ key: '', at: 0 });
  const progressKey = `${p.asked}:${p.scored?.ok ?? 0}:${p.scored?.fail ?? 0}`;
  useEffect(() => {
    if (!active || ended) return undefined;
    const wait = itemsRefreshWait(progressKey, lastRefresh.current, Date.now());
    if (wait === null) return undefined;
    const t = setTimeout(() => {
      lastRefresh.current = { key: progressKey, at: Date.now() };
      void load();
    }, wait);
    return () => clearTimeout(t);
  }, [progressKey, active, ended, load]);
  const flowNote = flowV2Notice(flow);
  return (
    <div className="space-y-4">
      <button type="button" className="text-sm text-blue-600 hover:underline" onClick={onBack}>
        ← All runs
      </button>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {flowNote && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">{flowNote}</div>}
      {run === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">Run of {fmtTime(run.createdAt)}</span>
            <span className="text-gray-500">by {run.requestedBy ?? '—'}</span>
            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100">
              {run.status}
              {active && run.cancelRequested ? ' (stopping)' : ''}
            </span>
            {active && (
              <button type="button" disabled={busy || run.cancelRequested} className="ml-auto text-red-600 hover:underline disabled:text-gray-400" onClick={() => void cancel()}>
                Cancel run
              </button>
            )}
          </div>
          {active && (
            <p>
              Asked {p.asked} of {s?.total ?? '…'}
              {p.errors ? ` · ${p.errors} without an answer` : ''}
              {p.scored ? ` · scored so far: ${p.scored.ok} right, ${p.scored.fail} wrong` : ''}. The run continues if you leave this page.
            </p>
          )}
          {active && live.streamError && <p className="text-xs text-amber-700">{live.streamError}</p>}
          {run.error && <p className="text-red-600">Error: {run.error}</p>}
          {s && !active && (
            <p>
              Score <b>{scoreText(s)}</b> · {s.ok} right · {s.fail} wrong · {s.error} without an answer
              {s.pending ? ` · ${s.pending} not scored` : ''}
              {s.capped ? ` · the first ${s.total} of ${s.available} questions` : ''}
              {s.runSeconds != null ? ` · took ${fmtDuration(s.runSeconds)}` : ''}
            </p>
          )}
          {s && !active && s.blockJudged ? (
            <p>
              Right block <b>{rateLabel(s)}</b> — the run chose the block each question names.
            </p>
          ) : null}
          <p className="text-gray-600">
            Claude cost {fmtUsd(run.usage?.costUsd)} (scoring and suggestions)
            {botTokens(run.models)
              .map((b) => ` · bot ${b.model}: ${b.input} in / ${b.output} out tokens`)
              .join('')}{' '}
            — internal, never billed to the customer.
          </p>
        </div>
      )}
      {run && !active && s && s.fail > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <button type="button" disabled={busy || suggesting} className="px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50" onClick={() => void suggest()}>
            {busy || suggesting ? 'Making suggestions…' : 'Suggest fixes'}
          </button>
          {outcome && (
            <span>
              {outcome}{' '}
              <Link className="text-blue-600 hover:underline" to={`/bots/${system}/${encodeURIComponent(botId)}/quality?tab=suggestions`}>
                Open Suggestions
              </Link>
            </span>
          )}
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {wrongBlocksFirst(items).map((it) => (
            <li key={it.questionId} className="bg-white border border-gray-200 rounded-lg p-3 text-sm space-y-1">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-1.5 py-0.5 rounded ${VERDICT_TONE[it.verdict]}`}>{VERDICT_LABEL[it.verdict]}</span>
                {blockVerdict(it) && (
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${it.blockRight ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    title={it.blockRight ? 'Right block' : 'Wrong block'}
                  >
                    {blockVerdict(it)} block
                  </span>
                )}
                {it.responseMs != null && <span className="text-xs text-gray-500">{(it.responseMs / 1000).toFixed(1)} s</span>}
              </div>
              <div>
                <span className="text-gray-500">Q:</span> {it.question}
              </div>
              <div className="whitespace-pre-wrap">
                <span className="text-gray-500">A:</span> {it.answer ?? '—'}
              </div>
              {blockLine(it, titles) && <div className="text-gray-700 text-xs">{blockLine(it, titles)}</div>}
              {it.reason && (
                <div className="text-gray-700">
                  <span className="text-gray-500">Why:</span> {it.reason}
                </div>
              )}
              {it.documents.length > 0 && (
                <div className="text-xs space-y-0.5">
                  {it.documents.map((d) =>
                    d.exists ? (
                      <div key={d.id}>
                        <Link className="text-blue-600 hover:underline" to={docLink(system, botId, d.id)}>
                          {d.title || `Document ${d.id}`}
                        </Link>
                        {docHint(d.origin) && <span className="text-amber-800"> — {docHint(d.origin)}</span>}
                      </div>
                    ) : (
                      <div key={d.id} className="text-gray-400">
                        Document {d.id} (no longer exists)
                      </div>
                    ),
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
